import { Button, Input, Text } from '@oc-labs/wt-storybook'
import toast from 'react-hot-toast';
import { useEffect, useState, useRef } from 'react';
import useAuth from '../../../hooks/useAuth';
import useCompany from '../../../hooks/useCompany';
import useNavigate from '../../../hooks/useNavigate';
import TurnstileCaptcha from '../../../components/TurnstileCaptcha';
import LinkParams from '../../../components/LinkParams';

export default function AuthLoginView() {
  // Declarar todos los hooks y estados al inicio
  const queryParams = new URLSearchParams(window.location.search);
  const idp = queryParams.get('idp');
  const emailFromQuery = queryParams.get('email');
  const iframe = queryParams.get('iframe');
  const { login, idpLogin } = useAuth();
  const { companies, refetchCompanies, refetchActiveCompany } = useCompany();
  const { navigate } = useNavigate();
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [email, setEmail] = useState(emailFromQuery || '');
  const [loading, setLoading] = useState(false);
  const [password, setPassword] = useState('');
  const [captcha, setCaptcha] = useState(null);
  const captchaRef = useRef(null);

  useEffect(() => {
    if (pendingRedirect === 'post-login') {
      const invitationToken = new URLSearchParams(window.location.search).get('organizationInvitationToken');
      if (invitationToken) {
        console.log('[LOGIN] Navegando a /auth/accept-invitation');
        navigate(`/auth/accept-invitation/${invitationToken}`);
        setPendingRedirect(null);
      } else if (companies && companies.length > 0) {
        console.log('[LOGIN] Navegando a /dashboard/home');
        navigate('/dashboard/home');
        setPendingRedirect(null);
      } else if (companies && companies.length === 0) {
        console.log('[LOGIN] Navegando a /auth/onboard');
        navigate('/auth/onboard');
        setPendingRedirect(null);
      }
    }
  }, [pendingRedirect, companies, navigate]);

  useEffect(() => {
    if (idp) {
      queryParams.delete('idp');
      window.history.replaceState({}, '', `${window.location.pathname}${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
      idpLogin(idp)
    }
  }, [])

  const handleLogin = async () => {
    if (!email || !password) return toast.error('Please enter both email and password');
    setLoading(true);

    try {
      await login(email, password, captcha);
      await refetchCompanies();
      await refetchActiveCompany();
      toast.success('Login successful');
      setPendingRedirect('post-login');
    } catch (error) {
      toast.error(error.message ||'Login failed');
    } finally {
      setCaptcha(null);
      captchaRef.current?.reset();
    }
    setLoading(false);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin()
    }
  }

  useEffect(() => {
    if (iframe) {
      window.parent.postMessage({
        type: 'loginLoaded'
      }, '*');
    }
  }, [iframe]);

  return (
    <div className='p-4 flex flex-col'>
      <div className="flex items-center space-x-2 mb-8 mt-4">
        <div className="text-lg font-semibold">
          <Text>Login to Your Account</Text>
        </div>
      </div>

      <div className="mb-4">
        <Input 
        label={<Text>Enter your email address</Text>}
        placeholder={"Enter your email"}
        onChange={(e) => setEmail(e.target.value)} value={email} type='text' />
      </div>

      <div className="mb-4">
        <Input
          label={<Text>Enter your password</Text>}
          type='password'
          placeholder="********"
          onChange={(e) => setPassword(e.target.value)}
          value={password}
          onKeyDown={handleKeyDown}
        />
      </div>

      <div className="text-center text-sm font-semibold cursor-pointer mb-8">
        <LinkParams to="/auth/password/recover">
          <Text>Forgot your password?</Text>
        </LinkParams>
      </div>

      <div className="mt-auto mb-4 space-y-4 relative">
        <Button type="primary" size="large" onClick={handleLogin} className="w-full" disabled={loading}>
          Log In
        </Button>

        <Button 
          type="tertiary" 
          size="large" 
          className={`w-full `} 
          disabled={loading} 
          onClick={() => idpLogin('google')}
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <Text>
            Continue with Google
          </Text>
        </Button>
        
        <TurnstileCaptcha 
          ref={captchaRef}
          onSuccess={(token) => setCaptcha(token)} 
          onError={() => console.error('Captcha error')}
        />

        <div className="flex justify-center">
          <Text className="text-sm text-gray-500">
            <LinkParams to="/auth/register" className="underline">
              Register a new account
            </LinkParams>
          </Text>
        </div>
      </div>
    </div>
  )
}